// src/pages/dashboard/branch/BranchLayout.tsx (Updated section)
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FiCoffee,
  FiAward,
  FiDollarSign,
  FiGrid,
  FiLayers,
  FiSettings,
  FiUsers,
  FiX,
  FiCameraOff,

} from "react-icons/fi";
import { RiRobot2Line } from "react-icons/ri";
import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  selectBranchLoading,
  selectCurrentBranch,
  selectCurrentOrganization,
  selectHasChanges,
  selectIsPublishing,
} from "../../../store/selectors";
import {
  clearCurrentBranch,
  fetchBranchById,
  publishBranch,
  setHasChanges,
} from "../../../store/slices/branchSlice";
import { clearCategories } from "../../../store/slices/categorySlice";
import { clearMenuItems } from "../../../store/slices/menuItemSlice";
import { useLocalizedBranch } from "../../../utils/brandHelper";
import DashboardNavbar from "../components/DashboardNavbar";
import Button from "../../../components/ui/Button";
import { BranchProvider, useBranch } from "../../../context/BranchContext";

const BranchLayout: React.FC = () => {
  const { branchId } = useParams<{ branchId: string }>();

  return (
    <BranchProvider branchId={branchId || null}>
      <BranchLayoutContent />
    </BranchProvider>
  );
};

const BranchLayoutContent: React.FC = () => {
  const { t } = useTranslation();
  const { branchId } = useParams<{ branchId: string }>();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isPublishing = useAppSelector(selectIsPublishing);
  const branch = useAppSelector(selectCurrentBranch);
  const isLoading = useAppSelector(selectBranchLoading);
  const dispatch = useAppDispatch();
  const organization = useAppSelector(selectCurrentOrganization);
  const { hasDietaryChanges, clearDietaryChanges } = useBranch();

  // Get localized branch data
  const localizedBranch = useLocalizedBranch(branch);
  const branchHasChanges = useAppSelector(selectHasChanges);
  const showPublishButton = branchHasChanges || hasDietaryChanges;

  useEffect(() => {
    return () => {
      dispatch(clearCurrentBranch());
      dispatch(clearCategories());
      dispatch(clearMenuItems());
    };
  }, []);

  useEffect(() => {
    if (!branchId) {
      navigate("/dashboard");
      return;
    }
    if (!isLoading && (!branch || branch.id !== branchId) && organization) {
      dispatch(fetchBranchById({ branchId, organizationId: organization.id }))
        .unwrap()
        .then((data) => {
          if (!data) navigate("/dashboard");
        })
        .catch((error) => {
          navigate("/dashboard");
          console.error(error);
        });
    }

    // return () => {
    // 	dispatch(clearCurrentBranch())
    // }
  }, [branchId, navigate, organization]);

  const navItems = [
    {
      path: `/dashboard/${branchId}`,
      label: t("sidebar.dashboard"),
      icon: <FiGrid className="w-5 h-5" />,
    },
    {
      path: `/dashboard/${branchId}/categories`,
      label: t("sidebar.categories"),
      icon: <FiLayers className="w-5 h-5" />,
    },
    {
      path: `/dashboard/${branchId}/menu`,
      label: t("sidebar.menu"),
      icon: <FiCoffee className="w-5 h-5" />,
    },
    {
      path: `/dashboard/${branchId}/menu-management`,
      label: t("sidebar.menuManagement"),
      icon: <FiAward className="w-5 h-5" />,
    },
    {
      path: `/dashboard/${branchId}/analytics`,
      label: t("sidebar.analytics"),
      icon: <FiUsers className="w-5 h-5" />,
    },
    {
      path: `/dashboard/${branchId}/agent`,
      label: t("sidebar.agent"),
      icon: <RiRobot2Line className="w-5 h-5" />,
    },
    {
      path: `/dashboard/${branchId}/settings`,
      label: t("sidebar.settings"),
      icon: <FiSettings className="w-5 h-5" />,
    },
    {
      path: `/dashboard/${branchId}/feedback`,
      label: t("sidebar.feedback"),
      icon: <img src="/icons/image.png" className="w-5 h-5" style={{ filter: "brightness(0) invert(1)" }} />,
    }

  ];

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.endsWith(`/${path}`);
  };

  const handlePublishMenu = async () => {
    if (!branchId) return;

    dispatch(publishBranch({ branchId, organizationId: organization?.id! }));
    clearDietaryChanges();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50 dark:bg-gray-900">
      {/* Sidebar for mobile */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -100, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-gray-600/75"
              onClick={() => setSidebarOpen(false)}
            />

            {/* Sidebar panel */}
            <div className="relative flex-1 flex flex-col max-w-xs w-full h-full pt-5 bg-white dark:bg-gray-800">
              <div className="absolute top-0 end-0 -me-12 pt-2">
                <button
                  className="ms-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white cursor-pointer"
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="sr-only">Close sidebar</span>
                  <FiX className="h-6 w-6 text-white" />
                </button>
              </div>

              <div className="flex-shrink-0 flex items-center px-4">
                <span className="text-2xl font-bold text-primary">
                  Dine.ai
                </span>
              </div>

              <div className="mt-5 flex-1 h-0 overflow-y-auto">
                <nav className="px-2 space-y-1">
                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActiveRoute(item.path)
                        ? "bg-primary/10 text-primary"
                        : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      {item.icon}
                      <span className="ms-3">{item.label}</span>
                    </Link>
                  ))}
                </nav>
              </div>

              {/* Branch info with localized data */}
              {branch && (
                <div className="flex-shrink-0 flex border-t border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex-shrink-0 w-full group block">
                    <div className="flex items-center">
                      <div>
                        {branch.logo ? (
                          <img
                            className="h-8 w-8 rounded-full"
                            src={branch.logo}
                            alt={localizedBranch.name || ""}
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="text-primary font-medium text-sm">
                              {localizedBranch.name?.charAt(0) || "?"}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="ms-3">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                          {localizedBranch.name || t("branches.untitled")}
                        </p>
                        <button
                          onClick={() => navigate("/dashboard")}
                          className="text-xs font-medium text-orange-500 hover:text-primary cursor-pointer"
                        >
                          {t("branches.change")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="p-1.5 text-xs text-center text-orange-500 border-t border-orange-200 dark:border-orange-700">
                Powered by: Nawaah
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-52">
          <div className="flex flex-col h-0 flex-1">
            <div className="flex items-center h-16 flex-shrink-0 px-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <img src="/images/logo/Dineai.png" alt="Dine.ai" className="ms-3 mx-3 h-8 w-auto" />

              <Link to="/dashboard" className="text-2xl font-bold text-primary">
                Dine.ai
              </Link>
              {/* Replace the following src with your actual logo path or swap for your app's Image component */}
            </div>
            <div className="flex-1 flex flex-col overflow-y-auto bg-white dark:bg-gray-800 border-e border-gray-200 dark:border-gray-700">
              <nav className="flex-1 px-2 py-4 space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActiveRoute(item.path)
                      ? "bg-primary/10 text-primary"
                      : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                      }`}
                  >
                    {item.icon}
                    <span className="ms-3">{item.label}</span>
                  </Link>
                ))}
              </nav>

              {/* Branch info with localized data */}
              {branch && (
                <div className="flex-shrink-0 flex border-t border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex-shrink-0 w-full group block">
                    <div className="flex items-center">
                      <div>
                        {branch.logo ? (
                          <img
                            className="h-8 w-8 rounded-full"
                            src={branch.logo}
                            alt={localizedBranch.name || ""}
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="text-primary font-medium text-sm">
                              {localizedBranch.name?.charAt(0) || "?"}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="ms-3">
                        <p className="text-sm font-medium text-orange-500 ">
                          {localizedBranch.name || t("branches.untitled")}
                        </p>
                        <button
                          onClick={() => navigate("/dashboard")}
                          className="text-xs font-medium text-orange-500 hover:text-primary cursor-pointer"
                        >
                          {t("branches.change")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="p-1.5 text-xs text-center text-gray-500  border-e border-t border-gray-200 dark:border-gray-700">
              Powered by: Nawaah
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <DashboardNavbar
          onMenuClick={() => setSidebarOpen(true)}
          showMenuButton={true}
        />

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          {!isLoading && showPublishButton && (
            <div className="sticky z-10 top-0 flex items-center justify-between border-b border-gray-600 px-4 sm:px-6 lg:px-8 py-1 bg-white dark:bg-gray-700 ">
              <div></div>
              <div className="relative flex flex-col">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handlePublishMenu}
                  isLoading={isPublishing}
                  className=" hover:bg-orange-500 bg-orange-700 hover:text-white animate-pulse hover:animate-none"
                >
                  {t("dashboard.publishChanges")}
                </Button>
              </div>
            </div>
          )}
          <div className="py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default BranchLayout;
